import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Shield } from "lucide-react";
import { motion, useMotionValue, useTransform, useMotionTemplate } from "framer-motion";
import { useTranslation } from "@/contexts/LanguageContext";
import { analytics } from "@/lib/analytics";

type LockedPreviewProps = {
  mode: "member" | "admin";
  moduleLabel?: string;
  customTitle?: string;
  customDescription?: string;
  children: React.ReactNode;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.4 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }
};

// Magnetic button wrapper component
type MagneticButtonProps = {
  children: React.ReactNode;
  className?: string;
};

function MagneticButton({ children, className }: MagneticButtonProps) {
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Magnetic pull strength (15% of distance)
    x.set((event.clientX - centerX) * 0.15);
    y.set((event.clientY - centerY) * 0.15);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={buttonRef}
      className={className}
      style={{ x, y }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      transition={{ type: "spring", stiffness: 350, damping: 15 }}
    >
      {children}
    </motion.div>
  );
}

export function LockedPreview({ mode, moduleLabel, customTitle, customDescription, children }: LockedPreviewProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isMember = mode === "member";

  // Mouse parallax for card
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Transform mouse position to rotation values (±8 degrees)
  const rotateX = useTransform(mouseY, [-300, 300], [8, -8]);
  const rotateY = useTransform(mouseX, [-300, 300], [-8, 8]);

  // Subtle translate for depth effect
  const translateX = useTransform(mouseX, [-300, 300], [-5, 5]);
  const translateY = useTransform(mouseY, [-300, 300], [-5, 5]);

  // Glow position tracking (0-100% of card)
  const glowX = useTransform(mouseX, [-300, 300], [0, 100]);
  const glowY = useTransform(mouseY, [-300, 300], [0, 100]);
  const glowBackground = useMotionTemplate`radial-gradient(400px circle at ${glowX}% ${glowY}%, hsl(var(--primary) / 0.15), transparent 60%)`;

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    mouseX.set(event.clientX - centerX);
    mouseY.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <div className="relative">
      {/* Real content - animates from clear to blurred */}
      <motion.div 
        initial={{ filter: "blur(0px)", opacity: 1 }}
        animate={{ filter: "blur(8px)", opacity: 0.4 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="select-none [&>*]:pointer-events-none [&_nav]:blur-0 [&_nav]:opacity-100 [&_nav]:pointer-events-auto [&_header]:blur-0 [&_header]:opacity-100 [&_header]:pointer-events-auto"
      >
        {children}
      </motion.div>

      {/* Shimmer overlay effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        {/* Primary shimmer - slower, primary color */}
        <motion.div
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ x: "200%", opacity: [0, 0.5, 0] }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatDelay: 2,
            ease: "easeInOut"
          }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/20 to-transparent skew-x-12"
        />
        
        {/* Secondary shimmer - faster, accent color */}
        <motion.div
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ x: "200%", opacity: [0, 0.3, 0] }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            repeatDelay: 3.5,
            ease: "easeInOut",
            delay: 1.2
          }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -skew-x-[8deg]"
        />
        
        {/* Tertiary shimmer - medium speed, JCI Teal */}
        <motion.div
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ x: "200%", opacity: [0, 0.25, 0] }}
          transition={{
            duration: 2.4,
            repeat: Infinity,
            repeatDelay: 4,
            ease: "easeInOut",
            delay: 2.5
          }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-[#57BCBC]/20 to-transparent skew-x-[16deg]"
        />
      </div>

      {/* CTA positioned after header/hero - only covers main content area */}
      <div 
        className="absolute left-0 right-0 top-[280px] flex justify-center p-6 z-30"
        style={{ perspective: 1000 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
          className="w-full max-w-xl"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.div
              style={{
                rotateX,
                rotateY,
                translateX,
                translateY,
                transformStyle: "preserve-3d"
              }}
              animate={{ 
                boxShadow: [
                  "0 10px 40px -10px hsl(var(--primary) / 0.2)",
                  "0 20px 60px -10px hsl(var(--primary) / 0.35)",
                  "0 10px 40px -10px hsl(var(--primary) / 0.2)"
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-lg"
            >
              <Card className="bg-background/95 backdrop-blur-xl border-2 border-primary/30 relative overflow-hidden group">
                {/* Cursor-following glow effect */}
                <motion.div
                  className="absolute inset-0 rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: glowBackground }}
                />
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <CardHeader>
                    <motion.div variants={itemVariants}>
                      <CardTitle className="flex items-center gap-2 text-foreground">
                        <motion.span
                          animate={{ scale: [1, 1.15, 1] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                        >
                          {isMember ? (
                            <Lock className="h-5 w-5 text-primary" />
                          ) : (
                            <Shield className="h-5 w-5 text-warning" />
                          )}
                        </motion.span>
                        {customTitle ?? (isMember
                          ? t('access.exclusiveMemberInsight')
                          : t('access.adminOnlyModule'))}
                      </CardTitle>
                    </motion.div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <motion.p variants={itemVariants} className="text-sm text-muted-foreground">
                      {customDescription ?? (isMember
                        ? t('access.joinCTA')
                        : t('access.adminOnlyMessage', { moduleLabel: moduleLabel ? `(${moduleLabel}) ` : '' }))}
                    </motion.p>

                    <motion.div variants={itemVariants}>
                      {isMember ? (
                        <div className="flex flex-col sm:flex-row gap-3">
                          <MagneticButton className="flex-1">
                            <Button 
                              className="w-full" 
                              onClick={() => {
                                analytics.ctaClick('join_now_login', 'locked_preview', '/auth');
                                navigate("/auth");
                              }}
                            >
                              {t('access.joinNowLogin')}
                            </Button>
                          </MagneticButton>
                          <MagneticButton className="flex-1">
                            <Button variant="outline" className="w-full border-primary/50 hover:bg-primary/10" asChild>
                              <Link to="/dashboard">{t('access.backToDashboard')}</Link>
                            </Button>
                          </MagneticButton>
                        </div>
                      ) : (
                        <MagneticButton className="w-full">
                          <Button variant="outline" className="w-full" asChild>
                            <Link to="/dashboard">{t('access.backToDashboard')}</Link>
                          </Button>
                        </MagneticButton>
                      )}
                    </motion.div>
                  </CardContent>
                </motion.div>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
